/**
 * PurchaseFlowEngine - Configuration-driven multi-step form engine
 *
 * Features:
 * - Dynamic step navigation based on Liquid-generated config
 * - Stack-based back navigation (tracks actual user path)
 * - Conditional step visibility based on payload values
 * - Pluggable validators and data collectors
 * - Klaviyo integration for form submission
 */
const STRING_FILTERS = {
  lowercase:   (v) => v.toLowerCase(),
  downcase:    (v) => v.toLowerCase(),
  uppercase:   (v) => v.toUpperCase(),
  upcase:      (v) => v.toUpperCase(),
  capitalize:  (v) => v.charAt(0).toUpperCase() + v.slice(1),
  titlecase:   (v) => v.replace(/\b\w/g, (c) => c.toUpperCase()),
  first_word:  (v) => v.split(/\s+/)[0] || v,
  last_word:   (v) => v.split(/\s+/).slice(1).join(' ') || '',
  handleize:   (v) => v.toLowerCase().replace(/\s+/g, '-'),
  trim:        (v) => v.trim(),
  reverse:     (v) => v.split('').reverse().join(''),
  split:       (v, sep) => v.split(sep || ',').map((s) => s.trim()).join(', '),
  first:       (v) => v.split(',').map((s) => s.trim())[0] || v,
  last:        (v) => { const parts = v.split(',').map((s) => s.trim()); return parts[parts.length - 1] || v; },
  truncate:    (v, n) => { const len = parseInt(n, 10); return isNaN(len) ? v : v.slice(0, len); },
};

class PurchaseFlowEngine {
  constructor(config) {
    this.config = config;
    this.steps = config.steps || [];
    this.payload = {};
    this.navigationStack = [];
    this.currentStepIndex = -1;
    this.currentStepId = null;
    this.stepBeforeLeave = null;
    this.autoAdvanceTimeout = null;
    this.interpolatedNodes = new WeakMap();
    this._navigating = false;

    this.container = document.querySelector('.purchase_flow_main_container');
    this.mainWrapper = document.querySelector('.purchase_flow_main');
    this.headerBackButton = document.querySelector('.purchase_flow_header .back-button');
    this.leaveScreen = document.querySelector('.flow_close_screen_content');

    // Cache step elements for fast lookup
    this._stepElements = new Map();
    this._allStepElements = [];
    this._inputDebounceTimer = null;

    // Data collectors keyed by generic step type
    this.dataCollectors = {
      'content': () => null,
      'input': this.collectInputData.bind(this),
      'auto-advance': () => null,
      'single-choice': this.collectButtonSelectionData.bind(this),
      'multi-choice': this.collectMultiChoiceData.bind(this),
      'radio': this.collectRadioData.bind(this),
      'subscription': () => null,
    };

    this.init();
  }

  init() {
    if (!this.container || !this.steps.length) {
      console.warn('PurchaseFlowEngine: No container or steps found');
      return;
    }

    this.cacheStepElements();
    this.bindEvents();
    this.showStep(0);
  }

  cacheStepElements() {
    this._allStepElements = Array.from(
      this.container.querySelectorAll('.purchase_flow_question')
    );
    this._allStepElements.forEach((el) => {
      const id = el.dataset.stepId;
      if (id) this._stepElements.set(id, el);
    });
  }

  getStepElement(stepId) {
    return this._stepElements.get(stepId) || null;
  }

  bindEvents() {
    // Delegate all button clicks within the flow container
    this.container.addEventListener('click', (e) => {
      const target = e.target.closest('[data-action]');
      if (!target) return;

      const action = target.dataset.action;
      this.handleAction(action, target);
    });

    // Back button in header
    if (this.headerBackButton) {
      this.headerBackButton.addEventListener('click', () => this.goBack());
    }

    // Input field changes - enable/disable submit buttons and clear validation errors (debounced)
    this.container.addEventListener('input', (e) => {
      const input = e.target;
      if (input.matches('input[type="text"], input[type="number"], input[type="email"], input[type="tel"]')) {
        this.hideValidationError(input.closest('.purchase_flow_question'));
        clearTimeout(this._inputDebounceTimer);
        this._inputDebounceTimer = setTimeout(() => {
          this.updateSubmitButtonState(input);
        }, 150);
      }
    });

    // Radio change - clear validation errors
    this.container.addEventListener('change', (e) => {
      if (e.target.matches('input[type="radio"]')) {
        this.hideValidationError(e.target.closest('.purchase_flow_question'));
      }
    });

    // Form submission for subscription step
    const subscriptionForm = document.getElementById('subscription_form');
    if (subscriptionForm) {
      subscriptionForm.addEventListener('submit', (e) => this.handleSubscriptionSubmit(e));
    }

    // Close/logo button handlers - reopen the leave screen, or exit when it is already open
    document.querySelectorAll('.close_setp_popup').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this.leaveScreen?.classList.contains('open_poup')) {
          window.location.href = this.getExitUrl();
          return;
        }
        this.showLeaveScreen();
      });
    });

    // "Stay" button on leave screen - return to flow
    document.querySelectorAll('.back_step_1').forEach((btn) => {
      btn.addEventListener('click', () => this.hideLeaveScreen());
    });

    // Country code selector change
    const countrySelect = document.getElementById('country_code');
    if (countrySelect) {
      countrySelect.addEventListener('change', (e) => {
        const currentCodeEl = document.getElementById('current_country_code');
        if (currentCodeEl) {
          currentCodeEl.textContent = e.target.value;
        }
      });
    }
  }

  handleAction(action, target) {
    switch (action) {
      case 'start':
        this.advance();
        break;
      case 'select-option':
        this.handleButtonSelection(target);
        break;
      case 'submit-input':
        this.handleInputSubmit(target);
        break;
      case 'continue':
        if (this.validateCurrentStep(target.closest('.purchase_flow_question'))) {
          this.advance();
        }
        break;
      case 'confirm':
        this.advance();
        break;
      case 'skip':
        this.skipStep();
        break;
      case 'toggle-option':
        this.handleToggleSelection(target);
        break;
      default:
        console.warn('Unknown action:', action);
    }
  }

  /**
   * Find step config by ID
   */
  getStepById(stepId) {
    return this.steps.find((s) => s.id === stepId);
  }

  /**
   * Find step index by ID
   */
  getStepIndexById(stepId) {
    return this.steps.findIndex((s) => s.id === stepId);
  }

  /**
   * Determine the next valid step based on current payload
   */
  findNextStep(fromIndex) {
    for (let i = fromIndex + 1; i < this.steps.length; i++) {
      const step = this.steps[i];
      if (this.shouldShowStep(step)) {
        return i;
      }
    }
    return -1; // No more steps
  }

  /**
   * Evaluate whether a step should be shown based on its show conditions
   */
  shouldShowStep(step) {
    const condition = step.showCondition;

    // No condition or operator is 'always' = always show
    if (!condition || !condition.field || condition.operator === 'always') {
      return true;
    }

    const fieldValue = this.payload[condition.field];
    const conditionValues = (condition.value || '')
      .split(',')
      .map((v) => v.trim().toLowerCase());
    // Normalize fieldValue: handleize converts spaces to hyphens (matching Liquid | handleize)
    const normalizedFieldValue = String(fieldValue || '').toLowerCase().replace(/\s+/g, '-');

    switch (condition.operator) {
      case 'equals':
        return conditionValues.includes(normalizedFieldValue);
      case 'not_equals':
        return !conditionValues.includes(normalizedFieldValue);
      case 'exists':
        return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
      case 'not_exists':
        return fieldValue === null || fieldValue === undefined || fieldValue === '';
      case 'less_than':
        return parseFloat(fieldValue) < parseFloat(condition.value);
      case 'greater_than':
        return parseFloat(fieldValue) > parseFloat(condition.value);
      default:
        return true;
    }
  }

  /**
   * Show a specific step by index
   */
  showStep(index) {
    if (index < 0 || index >= this.steps.length) return;

    // Clear any pending auto-advance
    if (this.autoAdvanceTimeout) {
      clearTimeout(this.autoAdvanceTimeout);
      this.autoAdvanceTimeout = null;
    }

    const step = this.steps[index];
    const stepElement = this.getStepElement(step.id);

    if (!stepElement) {
      console.warn(`Step element not found: ${step.id}`);
      // Try to advance to next valid step (with depth guard)
      const nextIndex = this.findNextStep(index);
      if (nextIndex >= 0) {
        this.showStep(nextIndex);
      } else {
        this._navigating = false;
      }
      return;
    }

    // Hide all steps (cached)
    this._allStepElements.forEach((el) => {
      el.classList.add('hidden');
    });

    // Show target step
    stepElement.classList.remove('hidden');
    this.currentStepIndex = index;
    this.currentStepId = step.id;

    // Interpolate {{variable}} placeholders with payload values
    this.interpolateVariables(stepElement);

    // Update back button visibility
    this.updateBackButton();

    // Handle auto-advance steps (like greeting messages)
    if (step.autoAdvance) {
      const delay = step.autoAdvanceDelay || 2000;
      this.autoAdvanceTimeout = setTimeout(() => {
        if (this.currentStepIndex === index) {
          // Reset lock so advance() can proceed
          this._navigating = false;
          this.advance();
        }
      }, delay);
      // Keep _navigating locked until auto-advance fires (prevents user double-advance)
    } else {
      // Release navigation lock for non-auto-advance steps
      this._navigating = false;
    }

    // Focus first input if present
    const firstInput = stepElement.querySelector(
      'input[type="text"], input[type="number"], input[type="email"], input[type="tel"]'
    );
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100);
    }
  }

  /**
   * Advance to the next valid step
   */
  advance() {
    if (this._navigating) return;
    this._navigating = true;

    const currentStep = this.steps[this.currentStepIndex];

    // Block advancement if required step has no valid data
    if (currentStep?.payloadField && !currentStep.optional) {
      const stepElement = this.getStepElement(currentStep.id);
      if (!this.validateCurrentStep(stepElement)) {
        this._navigating = false;
        return;
      }
    }

    // Collect data from current step
    this.collectCurrentStepData();
    if (this.currentStepIndex >= 0 && !(currentStep && currentStep.autoAdvance)) {
      this.navigationStack.push(this.currentStepIndex);
    }

    // Find next valid step
    const nextIndex = this.findNextStep(this.currentStepIndex);

    if (nextIndex >= 0) {
      this.showStep(nextIndex);
    } else {
      // Flow complete - this shouldn't happen if subscription is last
      console.log('Flow complete', this.payload);
      this._navigating = false;
    }
  }

  /**
   * Go back to previous step using navigation stack
   */
  goBack() {
    if (this._navigating) return;
    if (this.navigationStack.length === 0) return;

    this._navigating = true;

    // Clear any pending auto-advance from current step
    if (this.autoAdvanceTimeout) {
      clearTimeout(this.autoAdvanceTimeout);
      this.autoAdvanceTimeout = null;
    }

    const previousIndex = this.navigationStack.pop();
    this.showStep(previousIndex);
  }

  /**
   * Update back button visibility based on navigation stack
   */
  updateBackButton() {
    if (!this.headerBackButton) return;

    const iconWrap = this.headerBackButton.querySelector('.icon_wrap');
    if (this.navigationStack.length > 0) {
      this.headerBackButton.style.visibility = 'visible';
      if (iconWrap) iconWrap.style.visibility = 'visible';
    } else {
      this.headerBackButton.style.visibility = 'hidden';
      if (iconWrap) iconWrap.style.visibility = 'hidden';
    }
  }

  /**
   * Replace [[fieldName]] placeholders in text nodes with payload values.
   * Stores original text so re-interpolation works when navigating back.
   */
  interpolateVariables(stepElement) {
    const pattern = /\[\[([\w-]+(?:\|[^[\]]+)*)\]\]/g;
    const walker = document.createTreeWalker(stepElement, NodeFilter.SHOW_TEXT);
    const nodesToUpdate = [];

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const stored = this.interpolatedNodes.get(node);
      const text = stored || node.nodeValue;
      if (pattern.test(text)) {
        nodesToUpdate.push(node);
      }
      pattern.lastIndex = 0;
    }

    nodesToUpdate.forEach((node) => {
      if (!this.interpolatedNodes.has(node)) {
        this.interpolatedNodes.set(node, node.nodeValue);
      }
      const original = this.interpolatedNodes.get(node);
      node.nodeValue = original.replace(pattern, (match, expr) => {
        const parts = expr.split('|');
        const field = parts[0];
        const filters = parts.slice(1);
        let value = this.payload[field];
        if (value === undefined || value === null || value === '') {
          return match;
        }
        value = String(value);
        for (const f of filters) {
          const [name, ...args] = f.split(':');
          const fn = STRING_FILTERS[name.trim()];
          if (fn) value = fn(value, ...args);
        }
        return value;
      });
    });
  }

  /**
   * Collect data from the current step based on its type
   */
  collectCurrentStepData() {
    if (this.currentStepIndex < 0) return;

    const step = this.steps[this.currentStepIndex];
    const collector = this.dataCollectors[step.type];

    if (collector && step.payloadField) {
      const stepElement = this.getStepElement(step.id);
      const value = collector(stepElement, step);
      if (value !== undefined && value !== null) {
        this.payload[step.payloadField] = value;
      }
    }
  }

  // ==================== Data Collectors ====================

  collectInputData(element) {
    const input = element.querySelector('input[type="text"], input[type="number"], input[type="email"], input[type="tel"]');
    const value = input?.value?.trim() || '';
    return value || null;
  }

  collectButtonSelectionData(element) {
    const selected = element.querySelector('[data-action="select-option"].selected');
    return selected?.dataset?.value || null;
  }

  collectMultiChoiceData(element) {
    const selected = element.querySelectorAll('.selected_option_btn.button--grey-active');
    const values = Array.from(selected).map(
      (btn) => btn.dataset.value || btn.querySelector('span')?.textContent || btn.textContent.trim()
    );

    const customInput = element.querySelector('.input_sport');
    if (customInput?.value?.trim()) {
      values.push(customInput.value.trim());
    }

    return values.length > 0 ? values.join(', ') : null;
  }

  collectRadioData(element) {
    const checked = element.querySelector('input[type="radio"]:checked');
    return checked?.value || null;
  }

  // ==================== Event Handlers ====================

  handleButtonSelection(target) {
    const stepElement = target.closest('.purchase_flow_question');
    if (!stepElement) return;

    // Remove selection from siblings
    stepElement.querySelectorAll('[data-action="select-option"]').forEach((btn) => {
      btn.classList.remove('selected');
    });

    // Add selection to clicked button
    target.classList.add('selected');

    // Auto-advance after selection
    this.advance();
  }

  skipStep() {
    if (this._navigating) return;
    this._navigating = true;

    const currentStep = this.steps[this.currentStepIndex];
    if (!currentStep?.optional) {
      this._navigating = false;
      return;
    }
    if (this.currentStepIndex >= 0 && !(currentStep && currentStep.autoAdvance)) {
      this.navigationStack.push(this.currentStepIndex);
    }
    const nextIndex = this.findNextStep(this.currentStepIndex);
    if (nextIndex >= 0) {
      this.showStep(nextIndex);
    } else {
      this._navigating = false;
    }
  }

  validateCurrentStep(stepElement) {
    if (!stepElement) return true;
    const stepType = stepElement.dataset.stepType;
    let value = null;

    if (stepType === 'input') {
      const input = stepElement.querySelector('input[type="text"], input[type="number"], input[type="email"], input[type="tel"]');
      value = input?.value?.trim() || '';
      if (!value) return this.showValidationError(stepElement, false);
    } else if (stepType === 'single-choice') {
      const selected = stepElement.querySelector('[data-action="select-option"].selected');
      if (!selected) return this.showValidationError(stepElement, false);
      value = selected.dataset.value || '';
    } else if (stepType === 'radio') {
      const checked = stepElement.querySelector('input[type="radio"]:checked');
      if (!checked) return this.showValidationError(stepElement, false);
      value = checked.value || '';
    } else if (stepType === 'multi-choice') {
      const selected = stepElement.querySelectorAll('.selected_option_btn.button--grey-active');
      const customInput = stepElement.querySelector('.input_sport');
      if (selected.length === 0 && !customInput?.value?.trim()) return this.showValidationError(stepElement, false);
      value = Array.from(selected).map(btn => btn.dataset.value || btn.textContent.trim()).join(', ');
      if (customInput?.value?.trim()) value += (value ? ', ' : '') + customInput.value.trim();
    } else {
      return true;
    }

    // Regex validation (if pattern is set)
    const stepConfig = this.steps.find(s => s.id === stepElement.dataset.stepId);
    if (stepConfig?.validation) {
      try {
        const regex = new RegExp(stepConfig.validation);
        if (!regex.test(value)) return this.showValidationError(stepElement, true, stepConfig.validationError);
      } catch (e) {
        console.warn('Invalid validation regex:', stepConfig.validation, e);
        return this.showValidationError(stepElement, true, 'Validation configuration error');
      }
    }

    this.hideValidationError(stepElement);
    return true;
  }

  showValidationError(stepElement, isRegex, message) {
    const errorEl = stepElement?.querySelector('.validation_error');
    if (errorEl) {
      errorEl.textContent = message || 'This field is required';
      errorEl.classList.remove('hidden');
    }
    return false;
  }

  hideValidationError(stepElement) {
    if (!stepElement) return;
    const errorEl = stepElement.querySelector('.validation_error');
    if (errorEl) {
      errorEl.classList.add('hidden');
      errorEl.textContent = '';
    }
  }

  handleToggleSelection(target) {
    target.classList.toggle('button--grey-active');
    this.hideValidationError(target.closest('.purchase_flow_question'));
  }

  handleInputSubmit(target) {
    const stepElement = target.closest('.purchase_flow_question');
    if (!stepElement) return;
    const input = stepElement.querySelector('input[type="text"], input[type="number"], input[type="email"], input[type="tel"]');

    if (!input || !input.value.trim()) {
      return;
    }

    // Check if button is disabled
    if (target.classList.contains('disable')) {
      return;
    }

    this.advance();
  }

  updateSubmitButtonState(input) {
    const stepElement = input.closest('.purchase_flow_question');
    if (!stepElement) return;
    const submitBtn = stepElement.querySelector('[data-action="submit-input"], [data-action="continue"], .submit_last_btn');

    if (!submitBtn) return;

    // For subscription form, validate email format and phone length before enabling
    if (submitBtn.classList.contains('submit_last_btn')) {
      const form = submitBtn.closest('form');
      const email = form?.querySelector('[name="email"]')?.value?.trim() || '';
      const phone = form?.querySelector('[name="phone"]')?.value?.trim() || '';
      const emailValid = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(email);
      const countryCode = document.getElementById('current_country_code')?.textContent || '';
      const fullPhone = (countryCode + phone).replace(/\s/g, '');
      const phoneValid = fullPhone.replace(/\D/g, '').length >= 7;
      if (emailValid && phoneValid) {
        submitBtn.classList.remove('disable');
      } else {
        submitBtn.classList.add('disable');
      }
    } else {
      if (input.value.trim()) {
        submitBtn.classList.remove('disable');
      } else {
        submitBtn.classList.add('disable');
      }
    }
  }

  // ==================== Subscription Form ====================

  async handleSubscriptionSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const submitBtn = form.querySelector('.submit_last_btn');

    // Get form values
    const email = form.querySelector('[name="email"]')?.value?.trim();
    const phoneInput = form.querySelector('[name="phone"]')?.value?.trim();
    const countryCode = document.getElementById('current_country_code')?.textContent || '';
    const phone = (countryCode + phoneInput).replace(/\s/g, '');

    // Reset errors
    const emailError = form.querySelector('.emailError');
    const phoneError = form.querySelector('.phoneError');
    const formError = form.querySelector('.form_error');

    emailError?.classList.add('hidden');
    phoneError?.classList.add('hidden');
    formError?.classList.add('hidden');

    // Validation
    let isValid = true;

    if (!/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(email)) {
      emailError?.classList.remove('hidden');
      isValid = false;
    }

    if (!this.validatePhone(phone)) {
      if (formError) {
        formError.classList.remove('hidden');
        formError.textContent =
          window.PLETO?.t?.('purchase_flow.phone_number_error') || 'Please enter a valid phone number';
      } else {
        phoneError?.classList.remove('hidden');
      }
      isValid = false;
    }

    if (!isValid) return;

    // Show loading state
    submitBtn?.classList.add('loder');

    // Build final payload
    const fullName = this.payload.name || '';
    const nameParts = fullName.split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // Build dynamic properties from all payload fields except reserved keys
    const reservedKeys = new Set(['name', 'email', 'phone_number', 'first_name', 'last_name', '$locale', '$locale_language', '$locale_country', 'locale']);
    const dynamicProperties = Object.fromEntries(
      Object.entries(this.payload).filter(([key]) => !reservedKeys.has(key))
    );

    const profileData = {
      email,
      phone_number: phone,
      first_name: firstName,
      last_name: lastName,
      ...dynamicProperties,
      $locale: Shopify?.locale || 'es',
      $locale_language: Shopify?.locale || 'es',
      $locale_country: Shopify?.country || 'ES',
      locale: Shopify?.locale || 'es',
    };

    try {
      // Submit to Klaviyo
      await this.submitToKlaviyo(profileData, dynamicProperties);

      // Set completion cookie
      const maxAge = 365 * 24 * 60 * 60;
      document.cookie = `completed_flow=true; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;

      // Redirect
      let targetPath =
        window.PLETO?.product_redirect ||
        this.config.settings?.productRedirect ||
        '/products/pleto-subscription';
      const rootUrl = Shopify?.routes?.root?.replace(/\/$/, '') || '';

      // Strip rootUrl prefix from targetPath to avoid duplicate locale (e.g. /en/en/...)
      if (rootUrl && targetPath.startsWith(rootUrl)) {
        targetPath = targetPath.substring(rootUrl.length);
      }

      setTimeout(() => {
        window.location.href = rootUrl + targetPath + window.location.search + window.location.hash;
      }, 100);
    } catch (error) {
      console.error('Submission error:', error);
      submitBtn?.classList.remove('loder');
      if (formError) {
        formError.classList.remove('hidden');
        formError.textContent = 'An error occurred. Please try again.';
      }
    }
  }

  async submitToKlaviyo(profileData, dynamicProperties) {
    const companyId = this.config.settings?.klaviyoCompanyId || 'TvaAVr';
    const listId = this.config.settings?.klaviyoListId || 'QZHvWF';

    // Identify user with Klaviyo
    if (typeof klaviyo !== 'undefined' && klaviyo.identify) {
      try {
        await Promise.race([
          klaviyo.identify(profileData),
          new Promise((_, reject) => setTimeout(() => reject('timeout'), 2000)),
        ]);
      } catch (e) {
        console.warn('Klaviyo identify failed:', e);
      }

      // Track event
      if (klaviyo.track) {
        klaviyo.track('Purchase Flow Form Submitted').catch(() => {});
      }
    } else if (typeof _learnq !== 'undefined') {
      // Fallback to _learnq
      _learnq.push(['identify', profileData]);
      _learnq.push(['track', 'Purchase Flow Form Submitted']);
    }

    // Subscribe to list via API
    const subBody = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: profileData.email,
                phone_number: profileData.phone_number,
                first_name: profileData.first_name,
                last_name: profileData.last_name,
                properties: {
                  ...dynamicProperties,
                  $locale: profileData.$locale,
                  $locale_language: profileData.$locale_language,
                  $locale_country: profileData.$locale_country,
                },
                subscriptions: {
                  email: { marketing: { consent: 'SUBSCRIBED' } },
                  sms: {
                    marketing: { consent: 'SUBSCRIBED' },
                    transactional: { consent: 'SUBSCRIBED' },
                  },
                },
              },
            },
          },
          custom_source: 'Purchase Flow Form',
        },
        relationships: {
          list: { data: { type: 'list', id: listId } },
        },
      },
    };

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${companyId}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            revision: '2025-07-15',
            'content-type': 'application/vnd.api+json',
          },
          body: JSON.stringify(subBody),
        }
      );

      if (!response.ok) {
        throw new Error(`Klaviyo subscription API returned: ${response.status}`);
      }
    } catch (e) {
      console.warn('Klaviyo subscription failed:', e);
      throw e;
    }
  }

  validatePhone(value) {
    if (typeof libphonenumber !== 'undefined' && libphonenumber.isValidNumber) {
      return libphonenumber.isValidNumber(value);
    }
    // Fallback: basic length check
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly.length >= 7 && digitsOnly.length <= 15;
  }

  // ==================== Leave Screen ====================

  getExitUrl() {
    const exitLink = this.leaveScreen?.querySelector('.options_wrap a[href]');
    if (exitLink) return exitLink.getAttribute('href');

    const rootUrl = (typeof Shopify !== 'undefined' && Shopify?.routes?.root?.replace(/\/$/, '')) || '';
    const enableRedirect = this.mainWrapper?.dataset.enableProductRedirect === 'true';
    let productUrl = this.mainWrapper?.dataset.productUrl;
    if (!enableRedirect || !productUrl) return rootUrl + '/';
    if (rootUrl && productUrl.startsWith(rootUrl)) {
      productUrl = productUrl.substring(rootUrl.length);
    }
    return rootUrl + productUrl;
  }

  showLeaveScreen() {
    if (!this.leaveScreen) return;

    // Clear any pending auto-advance
    if (this.autoAdvanceTimeout) {
      clearTimeout(this.autoAdvanceTimeout);
      this.autoAdvanceTimeout = null;
    }

    this.stepBeforeLeave = this.currentStepIndex;

    // Hide all steps (cached)
    this._allStepElements.forEach((el) => {
      el.classList.add('hidden');
    });

    // Show leave screen
    this.leaveScreen.classList.remove('hidden');
    this.leaveScreen.classList.add('open_poup');
  }

  hideLeaveScreen() {
    if (!this.leaveScreen) return;

    this.leaveScreen.classList.add('hidden');
    this.leaveScreen.classList.remove('open_poup');

    if (this.stepBeforeLeave !== null && this.stepBeforeLeave !== undefined) {
      this.showStep(this.stepBeforeLeave);
    }
  }

  // ==================== Public API ====================

  /**
   * Get current payload
   */
  getPayload() {
    return { ...this.payload };
  }

  /**
   * Get current step info
   */
  getCurrentStep() {
    if (this.currentStepIndex < 0) return null;
    return this.steps[this.currentStepIndex];
  }

  /**
   * Reset the flow
   */
  reset() {
    // Clear any pending auto-advance
    if (this.autoAdvanceTimeout) {
      clearTimeout(this.autoAdvanceTimeout);
      this.autoAdvanceTimeout = null;
    }
    this._navigating = false;

    this.payload = {};
    this.navigationStack = [];
    this.currentStepIndex = -1;
    this.currentStepId = null;
    this.interpolatedNodes = new WeakMap();

    // Clear any selections
    this.container.querySelectorAll('.selected').forEach((el) => {
      el.classList.remove('selected');
    });
    this.container.querySelectorAll('.button--grey-active').forEach((el) => {
      el.classList.remove('button--grey-active');
    });

    // Clear inputs
    this.container.querySelectorAll('input').forEach((input) => {
      if (input.type !== 'radio') {
        input.value = '';
      } else {
        input.checked = false;
      }
    });

    this.showStep(0);
  }
}

// Initialize when DOM ready (singleton guard prevents double-init)
document.addEventListener('DOMContentLoaded', () => {
  if (window.purchaseFlowEngine) return;
  if (window.PLETO_FLOW_CONFIG && document.querySelector('.purchase_flow_main')) {
    window.purchaseFlowEngine = new PurchaseFlowEngine(window.PLETO_FLOW_CONFIG);
  }
});
